import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { 
  Users, 
  Store, 
  ClipboardCheck,
  ArrowUpRight,
  AlertCircle,
  Languages
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useCommunity } from '@/contexts/CommunityContext';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useIsMobile } from '@/hooks/use-mobile';
import { isTranslationAvailable } from '@/utils/translationUtils';

interface BusinessStats {
  total: number;
  pendingApproval: number;
}

interface ReviewStats {
  pendingApproval: number;
}

interface UserStats {
  total: number;
}

interface UserBusiness {
  id: string;
}

const Dashboard = () => {
  const { user } = useAuth();
  const { userRole, currentCommunity } = useCommunity();
  const { isStripeActive } = useIntegrations();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const hasAccess = userRole === 'superadmin' || userRole === 'admin' || userRole === 'reviewer';
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  
  const { data: userBusiness, isLoading: isLoadingUserBusiness } = useQuery<UserBusiness | null>({
    queryKey: ['userBusiness', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('businesses')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (error) {
        console.error('Error fetching user business:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user
  });
  
  const hasDashboardAccess = hasAccess || !!userBusiness;
  
  const { data: businessStats, isLoading: isLoadingBusinesses } = useQuery<BusinessStats>({
    queryKey: ['admin', 'business-stats', currentCommunity.id],
    queryFn: async () => {
      const { data: totalData, error: totalError, count: totalCount } = await supabase
        .from('businesses')
        .select('*', { count: 'exact' })
        .eq('community_id', currentCommunity.id);
      
      if (totalError) throw totalError;
      
      // Updated: Using is_active=false instead of status='Pending Validation'
      const { data: pendingData, error: pendingError, count: pendingCount } = await supabase
        .from('businesses')
        .select('*', { count: 'exact' })
        .eq('community_id', currentCommunity.id)
        .eq('is_active', false);
        
      if (pendingError) throw pendingError;
      
      return {
        total: totalCount || 0,
        pendingApproval: pendingCount || 0
      };
    },
    enabled: hasDashboardAccess && !!currentCommunity.id
  });

  const { data: reviewStats, isLoading: isLoadingReviews } = useQuery<ReviewStats>({
    queryKey: ['admin', 'review-stats', currentCommunity.id],
    queryFn: async () => {
      // First, get the business IDs for the current community
      const { data: businessIds, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('community_id', currentCommunity.id);
      
      if (businessError) throw businessError;
      
      // If there are no businesses, return 0 pending reviews
      if (!businessIds || businessIds.length === 0) {
        return {
          pendingApproval: 0
        };
      }
      
      // Extract just the IDs into an array
      const ids = businessIds.map(b => b.id);
      
      // Now count pending reviews for these businesses
      const { count, error } = await supabase
        .from('reviews')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Pending Validation')
        .in('business_id', ids);
      
      if (error) throw error;
      
      return {
        pendingApproval: count || 0
      };
    },
    enabled: hasDashboardAccess && !!currentCommunity.id
  });

  const { data: userStats, isLoading: isLoadingUsers } = useQuery<UserStats>({
    queryKey: ['admin', 'user-stats', currentCommunity.id],
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from('community_members')
        .select('*', { count: 'exact' })
        .eq('community_id', currentCommunity.id)
        .eq('role_name', 'user');
      
      if (error) throw error;
      
      return {
        total: count || 0
      };
    },
    enabled: hasDashboardAccess && !!currentCommunity.id
  });

  const { data: translationStats, isLoading: isLoadingTranslations } = useQuery({
    queryKey: ['admin', 'translation-stats', currentCommunity.id],
    queryFn: async () => {
      const { data: defaultKeys, error: defaultError } = await supabase
        .from('translation_defaults')
        .select('key, default_value');
      
      if (defaultError) throw defaultError;
      
      const totalKeys = defaultKeys?.length || 0;
      
      const { data: existingTranslations, error: translationsError } = await supabase
        .from('community_translations')
        .select('translation_key, locale, value')
        .eq('community_id', currentCommunity.id);
        
      if (translationsError) throw translationsError;
      
      const localeStats = currentCommunity.available_locales.reduce((acc, locale) => {
        let missingCount = 0;
        
        defaultKeys?.forEach(defaultKey => {
          const communityTranslation = existingTranslations?.find(
            t => t.translation_key === defaultKey.key && t.locale === locale
          );
          
          const hasTranslation = isTranslationAvailable(
            communityTranslation?.value,
            defaultKey.default_value,
            locale
          );
          
          if (!hasTranslation) {
            missingCount++;
          }
        });
        
        acc[locale] = {
          total: totalKeys,
          missing: missingCount
        };
        return acc;
      }, {} as Record<string, { total: number; missing: number }>);
      
      const totalMissing = Object.values(localeStats).reduce((sum, stat) => sum + stat.missing, 0);
      
      return {
        byLocale: localeStats,
        totalMissing
      };
    },
    enabled: isAdmin && !!currentCommunity.id
  });

  const navigateToPendingBusinesses = () => {
    navigate('/admin/businesses?tab=pending');
  };
  
  const navigateToPendingReviews = () => {
    navigate('/admin/reviews?tab=pending');
  };

  const navigateToTranslations = () => {
    navigate('/admin/translations');
  };

  if (!hasDashboardAccess) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Card className="max-w-md p-6 text-center">
          <div className="mb-4 flex justify-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">
            You don't have permission to view the dashboard analytics.
          </p>
        </Card>
      </div>
    );
  }

  const isLoading = isLoadingBusinesses || isLoadingReviews || isLoadingUsers || isLoadingTranslations;

  const infoStatCards = [
    { 
      title: 'Total Businesses', 
      value: isLoading ? '-' : businessStats?.total || 0, 
      icon: Store, 
      color: 'from-amber-500 to-orange-600' 
    },
    { 
      title: 'Community Users', 
      value: isLoading ? '-' : userStats?.total || 0, 
      icon: Users, 
      color: 'from-blue-500 to-indigo-600' 
    },
  ];
  
  const actionCards = [
    {
      title: 'Businesses To Approve',
      value: isLoading ? '-' : businessStats?.pendingApproval || 0,
      icon: Store,
      color: 'from-purple-500 to-violet-600',
      onClick: navigateToPendingBusinesses,
      actionText: 'View pending businesses',
      show: hasAccess
    },
    {
      title: 'Reviews To Approve',
      value: isLoading ? '-' : reviewStats?.pendingApproval || 0,
      icon: ClipboardCheck,
      color: 'from-green-500 to-emerald-600',
      onClick: navigateToPendingReviews,
      actionText: 'View pending reviews',
      show: hasAccess
    },
    {
      title: 'Missing Translations',
      value: isLoadingTranslations ? '-' : translationStats?.totalMissing || 0,
      icon: Languages,
      color: 'from-sky-500 to-blue-600',
      onClick: navigateToTranslations,
      actionText: 'Manage translations',
      show: isAdmin && (translationStats?.totalMissing || 0) > 0
    }
  ];

  const filteredActionCards = actionCards.filter(card => card.show);

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* Enhanced Header */}
      <div className="relative">
        <h1 className="admin-section-title mb-2">Dashboard Overview</h1>
        <p className="text-muted-foreground/80 text-lg">
          Welcome back, <span className="font-semibold text-foreground">{user?.user_metadata?.full_name || 'User'}</span>
        </p>
      </div>

      {/* Analytics Section */}
      <div>
        <h2 className="admin-section-title mb-4">Analytics</h2>
        <div className="admin-grid md:grid-cols-2">
          {infoStatCards.map((stat, index) => (
            <Card key={index} className={`admin-card admin-card-stats animate-fade-in stagger-${index + 1} `}>
              <CardContent className="admin-stat-card">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="admin-stat-label mb-3">{stat.title}</p>
                    <div className="flex items-baseline gap-2">
                      <p className="admin-stat-value">{stat.value}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-xl ${
                      stat.title.includes('Business') 
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200' 
                        : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200'
                    }`}>
                      <stat.icon className={`h-6 w-6 ${
                        stat.title.includes('Business') ? 'text-blue-600' : 'text-emerald-600'
                      }`} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Pending Actions Section */}
      {filteredActionCards.length > 0 && (
        <div>
          <h2 className="admin-section-title mb-4">Pending Actions</h2>
          <div className="admin-grid lg:grid-cols-3">
            {filteredActionCards.map((card, index) => (
              <Card 
                key={index} 
                className={`admin-card admin-card-action animate-fade-in stagger-${index + 3} `}
                onClick={card.onClick}
              >
                <CardContent className="admin-stat-card">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <p className="admin-stat-label mb-3">{card.title}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-2xl sm:text-3xl font-bold text-foreground">{card.value}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className={`p-3 rounded-xl ${
                        card.title.includes('Business') 
                          ? 'bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200' 
                          : card.title.includes('Review') 
                          ? 'bg-gradient-to-br from-green-50 to-green-100 border border-green-200'
                          : 'bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200'
                      }`}>
                        <card.icon className={`h-6 w-6 ${
                          card.title.includes('Business') ? 'text-purple-600' 
                          : card.title.includes('Review') ? 'text-green-600' 
                          : 'text-sky-600'
                        }`} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm font-medium text-slate-600">{card.actionText}</span>
                    <ArrowUpRight className="h-4 w-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="mt-8 flex justify-center">
          <div className="flex space-x-2">
            <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
            <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
            <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
